<template>
  <v-dialog v-model="showEditPopup" persistent scrollable max-width="920" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-form class="fdlg__form" @submit.prevent="updateEquipment">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">Editează echipamentul</h2>
            <p v-if="equipment && equipment.model" class="fdlg__sub">
              {{ equipment.model }}<template v-if="equipment.registration_number">, nr. inventar {{ equipment.registration_number }}</template>
            </p>
          </div>
          <v-btn icon aria-label="Închide" @click="showEditPopup = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-desktop-classic</v-icon>Echipamentul</div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="selectedType"
                  :items="typeOptions"
                  label="Tip echipament*"
                  item-text="type"
                  item-value="id"
                  prepend-inner-icon="mdi-shape-outline"
                  outlined
                  required
                  :error-messages="formErrors.selectedType"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-switch
                  v-model="isFunctional"
                  label="Echipamentul este activ"
                  inset
                  color="primary"
                  class="mt-2"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="6">
                <v-combobox
                  v-model="model"
                  :items="modelSuggestions"
                  :search-input.sync="modelSearch"
                  label="Model*"
                  :hint="modelSuggestions.length ? 'Alegeți din catalog sau scrieți modelul' : ''"
                  prepend-inner-icon="mdi-tag-outline"
                  outlined
                  :error-messages="formErrors.model"
                ></v-combobox>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="registration"
                  label="Nr. inventar (opțional)"
                  prepend-inner-icon="mdi-barcode"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <SerialField ref="serial" v-model="serial" :error-messages="formErrors.serial" />
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-account-tie-outline</v-icon>Responsabil și departament</div>
            <v-row dense>
              <v-col v-if="isAdmin || isEngineer || isDtiSef" cols="12">
                <v-autocomplete
                  v-model="selectedUser"
                  :items="ownerOptions"
                  label="Persoana responsabilă*"
                  item-text="user"
                  item-value="id"
                  prepend-inner-icon="mdi-account-outline"
                  outlined
                  required
                  :error-messages="formErrors.selectedUser"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12">
                <div class="dept-field">
                  <div class="dept-field__bar">
                    <span class="dept-field__label">Departamentul*</span>
                    <span class="dept-field__current">
                      <template v-if="selectedDepartment && selectedDepartment.name">
                        <v-icon small>mdi-check-circle-outline</v-icon>{{ selectedDepartment.name }}
                        <span v-if="selectedDepartment.is_hidden" class="tag-hidden">ascuns</span>
                      </template>
                      <template v-else>bifați un singur departament</template>
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn outlined small color="primary" @click="resetDepartment">
                      <v-icon left small>mdi-restore</v-icon>Resetează
                    </v-btn>
                  </div>
                  <div class="dept-field__tree">
                    <v-treeview
                      :items="departmentTree"
                      item-key="id"
                      item-text="name"
                      selectable
                      selection-type="independent"
                      dense
                      open-on-click
                      selected-color="primary"
                      :value="selectedDepartment ? [selectedDepartment.id] : []"
                      @input="updateSelectedDepartment"
                      aria-label="Departamentul"
                    >
                      <template #label="{ item }">
                        {{ item.name }}<span v-if="item.hidden" class="tag-hidden">ascuns</span>
                      </template>
                    </v-treeview>
                  </div>
                </div>
                <div v-if="formErrors.selectedDepartment" class="form-error mt-2">
                  {{ formErrors.selectedDepartment }}
                </div>
                <p v-else-if="departmentHint" class="form-section__note mt-2 mb-0" data-note="department">
                  <v-icon small>mdi-information-outline</v-icon> {{ departmentHint }}
                </p>
              </v-col>
            </v-row>
            <div class="mb-3"></div>
          </section>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-map-marker-outline</v-icon>Locația</div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="selectedBlock"
                  :items="blocks"
                  label="Bloc*"
                  item-text="block"
                  item-value="block"
                  prepend-inner-icon="mdi-office-building-outline"
                  outlined
                  required
                  @change="onBlockChange"
                  :error-messages="formErrors.selectedBlock"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="selectedFloor"
                  :items="floors"
                  label="Etaj*"
                  item-text="floor"
                  item-value="floor"
                  prepend-inner-icon="mdi-stairs"
                  outlined
                  required
                  @change="onFloorChange"
                  :error-messages="formErrors.selectedFloor"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="selectedRoom"
                  :items="rooms"
                  label="Cameră*"
                  item-text="room"
                  item-value="room"
                  prepend-inner-icon="mdi-door"
                  outlined
                  required
                  :error-messages="formErrors.selectedRoom"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="locationDetails"
                  label="Detalii amplasare (opțional)"
                  hint="unde anume în cameră, de exemplu: hol, deasupra ușii 208"
                  prepend-inner-icon="mdi-map-marker-radius-outline"
                  outlined
                  counter="255"
                  maxlength="255"
                ></v-text-field>
              </v-col>
            </v-row>
          </section>

          <ItemPhotosSection
            :label.sync="labelPhotos"
            :place.sync="placePhotos"
            @uploading="photosUploading = $event"
            @error="$emit('error', $event)"
          />
        </v-card-text>

        <div class="fdlg__foot">
          <v-spacer></v-spacer>
          <v-btn text large @click="showEditPopup = false">Anulare</v-btn>
          <v-btn class="btn-green" large type="submit" :loading="saving" :disabled="photosUploading">
            <v-icon left>mdi-content-save-outline</v-icon>Salvează
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import { mapGetters } from "vuex";
import { offeredTypes, offeredLocations, departmentTree } from "./pickers";
import departmentHint from "./departmentHint";
import SerialField from "./SerialField.vue";
import ItemPhotosSection from "./ItemPhotosSection.vue";

const photoList = (file) => (file && file.id ? [file] : []);
const photoId = (list) => (list && list.length ? list[0].id : null);

export default {
  components: { SerialField, ItemPhotosSection },
  mixins: [departmentHint],
  props: {
    equipment: Object,
  },
  data() {
    return {
      saving: false,
      serial: "",
      locationDetails: "",
      // [{ id, width, height }]: the item's photos, or new uploads that the save links
      labelPhotos: [],
      placePhotos: [],
      photosUploading: false,
      users: [],
      types: [],
      // raw location rows; the pickers drop hidden rooms except the item's current one
      blockRows: [],
      floorRows: [],
      roomRows: [],
      departments: [],
      isFunctional: true,
      showEditPopup: false,
      selectedUser: null,
      ownerId: null,
      selectedType: null,
      selectedBlock: null,
      selectedFloor: null,
      selectedRoom: null,
      selectedDepartment: null,
      model: "",
      // what is typed in the model box before it loses focus
      modelSearch: null,
      modelSuggestions: [],
      registration: "",
      editedEquipment: { ...this.equipment },
      formErrors: {
        selectedUser: "",
        selectedType: "",
        model: "",
        registration: "",
        selectedBlock: "",
        selectedFloor: "",
        selectedRoom: "",
        selectedDepartment: "",
        serial: "",
      },
    };
  },
  methods: {
    show() {
      this.showEditPopup = true;
      this.loadModelSuggestions();
    },
    async loadModelSuggestions() {
      const typeId = this.selectedType;
      if (!typeId) {
        this.modelSuggestions = [];
        return;
      }
      try {
        const names = await api.getModelSuggestions(typeId);
        if (this.selectedType === typeId) this.modelSuggestions = names;
      } catch (error) {
        this.modelSuggestions = [];
      }
    },
    modelText() {
      const text = this.modelSearch != null ? this.modelSearch : this.model;
      return String(text || "").trim();
    },
    async fetchData() {
      try {
        this.types = await api.fetchEquipmentTypes();
        this.users = await api.getUsersDropDown();
        this.departments = await api.getDepartments();
        // the item may have been given before the departments arrived (dialog mounted on first use)
        if (this.equipment && this.equipment.departmentId && !this.selectedDepartment) {
          this.selectedDepartment = this.departments.find(
            (department) => String(department.id) === String(this.equipment.departmentId.id)
          ) || null;
        }
        this.blockRows = await api.fetchBlocks();
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$emit("error", error);
      }
    },
    async fetchFloors() {
      if (this.selectedBlock) {
        try {
          this.floorRows = await api.getFloorsByBlock(this.selectedBlock);
        } catch (error) {
          console.error("Error fetching floors:", error);
          this.$emit("error", error);
        }
      } else {
        this.floorRows = [];
      }
    },
    async fetchRooms() {
      if (this.selectedBlock && this.selectedFloor) {
        try {
          this.roomRows = await api.getRoomsByBlockAndFloor(
            this.selectedBlock,
            this.selectedFloor
          );
        } catch (error) {
          console.error("Error fetching rooms:", error);
          this.$emit("error", error);
        }
      } else {
        this.roomRows = [];
      }
    },
    // A floor/room from the previous block is not valid in the new one
    onBlockChange() {
      this.selectedFloor = null;
      this.selectedRoom = null;
      this.roomRows = [];
    },
    onFloorChange() {
      this.selectedRoom = null;
    },
    async updateEquipment() {
      if (this.saving || this.photosUploading) return;
      this.validateForm();

      if (!this.isFormValid()) {
        return;
      }

      this.saving = true;
      try {
        this.editedEquipment.id = this.equipment.id;
        const locationId = await api.getLocationIdByDetails(
          this.selectedBlock,
          this.selectedFloor,
          this.selectedRoom
        );
        if (!locationId) {
          this.formErrors.selectedRoom =
            "Locația (bloc / etaj / cameră) nu există. Reselectați camera.";
          return;
        }

        const equipmentDataToUpdate = {
          id: this.editedEquipment.id,
          equipment_type_id: this.selectedType,
          model: this.modelText(),
          registration_number: this.registration,
          responsible_person_id: this.selectedUser,
          location_id: locationId,
          department_id: this.selectedDepartment
            ? this.selectedDepartment.id
            : null,
          isFunctional: this.isFunctional,
          serial_number: this.serial.trim() || null,
          location_details: this.locationDetails.trim() || null,
        };
        // photos: only when changed (a new upload is linked, null removes it)
        const label = photoId(this.labelPhotos);
        if (label !== photoId(photoList(this.equipment.label_file))) equipmentDataToUpdate.label_file_id = label;
        const place = photoId(this.placePhotos);
        if (place !== photoId(photoList(this.equipment.place_file))) equipmentDataToUpdate.place_file_id = place;

        await api.updateEquipment(equipmentDataToUpdate);
        this.showEditPopup = false;

        this.$emit("updateEquipmentData");
        this.$emit("success", "Echipamentul a fost salvat");
      } catch (error) {
        console.error("Error updating equipment:", error);
        const serialError = api.serialErrorMessage(error);
        if (serialError) {
          // under the field: the message names the item that has this seria
          this.formErrors.serial = serialError;
          this.$nextTick(() => this.$refs.serial && this.$refs.serial.$el.scrollIntoView({ behavior: "smooth", block: "center" }));
        } else {
          this.$emit("error", error);
        }
      } finally {
        this.saving = false;
      }
    },
    updateSelectedDepartment(newValues) {
      // one department: the node just ticked replaces the previous one
      const currentId = this.selectedDepartment ? this.selectedDepartment.id : null;
      const picked = newValues.filter((id) => id !== currentId);
      if (picked.length > 0) {
        newValues = [picked[picked.length - 1]];
      }
      if (newValues.length === 1) {
        const selectedDepartmentId = newValues[0];
        this.selectedDepartment = this.departments.find(
          (department) => department.id === selectedDepartmentId
        );
      } else {
        this.selectedDepartment = null;
      }
    },
    resetForm() {
      this.formErrors = {
        selectedUser: "",
        selectedType: "",
        model: "",
        registration: "",
        selectedBlock: "",
        selectedFloor: "",
        selectedRoom: "",
        selectedDepartment: "",
        serial: "",
      };
    },
    resetDepartment() {
      this.selectedDepartment = null;
      this.formErrors.selectedDepartment = "";
    },
    isFormValid() {
      return !Object.values(this.formErrors).some((error) => error);
    },
    validateForm() {
      this.formErrors.selectedUser =
        (this.isAdmin || this.isEngineer || this.isDtiSef) && !this.selectedUser
          ? "Selectați o persoană responsabilă."
          : "";
      this.formErrors.selectedType = !this.selectedType
        ? "Selectați un tip de echipament."
        : "";
      this.formErrors.model = !this.modelText() ? "Introduceți un model." : "";
      this.formErrors.selectedBlock = !this.selectedBlock
        ? "Selectați un bloc."
        : "";
      this.formErrors.selectedFloor = !this.selectedFloor
        ? "Selectați un etaj."
        : "";
      this.formErrors.selectedRoom = !this.selectedRoom
        ? "Selectați o cameră."
        : "";
      this.formErrors.selectedDepartment = !this.selectedDepartment || !this.selectedDepartment.id
        ? "Alegeți departamentul."
        : "";
      this.formErrors.serial = this.$refs.serial ? this.$refs.serial.validate() : "";
    },
  },
  watch: {
    showEditPopup(newVal) {
      if (newVal) {
        helper.scrollToTop(this);
      } else {
        this.resetForm();
      }
    },
    equipment: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          // Find the user ID based on the responsable_person name
          if (this.users.length > 0) {
            const matchedUser = newValue.responsable_person && this.users.find(
              user => user.user === newValue.responsable_person
            );
            this.ownerId = matchedUser ? matchedUser.id : null;
            this.selectedUser = this.ownerId;
          } else {
            // Store the user info to set it once users are loaded
            this._pendingResponsablePerson = newValue.responsable_person;
          }
          
          this.selectedType = newValue.equipmentTypeId || this.types.find(
            (type) => String(type.type) === String(newValue.equipmentType)
          )?.id || null;
          
          if (newValue.departmentId != null) {
            this.selectedDepartment = this.departments.find(
              (department) =>
                String(department.id) === String(newValue.departmentId.id)
            );
          } else {
            this.selectedDepartment = null;
          }

          this.selectedBlock = newValue.block;
          this.selectedFloor = newValue.floor;
          this.selectedRoom = newValue.room;
          this.model = newValue.model;
          this.registration = newValue.registration_number;
          this.isFunctional = newValue.isFunctional;
          this.serial = newValue.serial_number || "";
          this.locationDetails = newValue.location_details || "";
          this.labelPhotos = photoList(newValue.label_file);
          this.placePhotos = photoList(newValue.place_file);
        }
      },
    },
    users: {
      handler(newUsers) {
        if (newUsers.length > 0 && this._pendingResponsablePerson) {
          const matchedUser = newUsers.find(
            user => user.user === this._pendingResponsablePerson
          );
          this.ownerId = matchedUser ? matchedUser.id : null;
          this.selectedUser = this.ownerId;
          this._pendingResponsablePerson = null;
        }
      }
    },
    selectedUser(newValue) {
      if (newValue) {
        this.editedEquipment.responsible_person_id = newValue;
        this.formErrors.selectedUser = "";
      } else {
        this.editedEquipment.responsible_person_id = null;
      }
    },
    selectedType(newValue) {
      if (newValue) {
        this.editedEquipment.equipment_type_id = newValue;
        this.formErrors.selectedType = "";
      } else {
        this.editedEquipment.equipment_type_id = null;
      }
      if (this.showEditPopup) this.loadModelSuggestions();
    },
    model() {
      this.formErrors.model = "";
    },
    serial() {
      this.formErrors.serial = "";
    },
    modelSearch() {
      this.formErrors.model = "";
    },
    selectedBlock(newValue) {
      if (newValue) {
        this.fetchFloors();
        this.formErrors.selectedBlock = "";
      } else {
        this.floorRows = [];
      }
    },
    selectedFloor(newValue) {
      if (newValue) {
        this.fetchRooms();
        this.formErrors.selectedFloor = "";
      } else {
        this.roomRows = [];
      }
    },
    selectedRoom(newVal) {
      this.formErrors.selectedRoom = "";
    },
    selectedDepartment(newVal) {
      this.formErrors.selectedDepartment = "";
    },
  },
  computed: {
    ...mapGetters(["getUser", "isAdmin", "isEngineer", "isDtiSef"]),
    // hidden types / rooms / departments are not offered, except the item's current one
    typeOptions() {
      return offeredTypes(this.types, this.equipment && this.equipment.equipmentTypeId);
    },
    currentLocation() {
      return this.equipment
        ? { block: this.equipment.block, floor: this.equipment.floor, room: this.equipment.room }
        : null;
    },
    blocks() {
      return helper.sortNatural(offeredLocations(this.blockRows, this.currentLocation), "block");
    },
    floors() {
      return offeredLocations(this.floorRows, this.currentLocation);
    },
    rooms() {
      return offeredLocations(this.roomRows, this.currentLocation);
    },
    // blocked accounts aren't offered, but the current owner stays selectable
    ownerOptions() {
      return this.users.filter((u) => u.isActive !== false || u.id === this.ownerId);
    },
    departmentTree() {
      const current = this.equipment && this.equipment.departmentId;
      return departmentTree(this.departments, current ? current.id : null);
    },
    // same soft hint as Echipament nou: the department stays a free choice
    hintOwnerId() {
      return this.selectedUser;
    },
    hintDepartmentId() {
      return this.selectedDepartment ? this.selectedDepartment.id : null;
    },
    hintActive() {
      return this.showEditPopup;
    },
  },
  mounted() {
    this.editedEquipment = { ...this.equipment };
    this.fetchData();
  },
};
</script>

<style scoped>
.dept-field {
  border: 1px solid #c3d0c9;
  border-radius: 10px;
  overflow: hidden;
}
.dept-field__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  padding: 8px 10px 8px 14px;
  background: var(--mint-50);
  border-bottom: 1px solid var(--line-soft);
}
.dept-field__label {
  font-weight: 700;
  color: var(--ink-2);
}
.dept-field__current {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--ink-3);
}
.dept-field__current .v-icon {
  color: var(--green);
}
.dept-field__tree {
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 0;
}
.tag-hidden {
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--slate-bg);
  color: var(--slate);
  font-size: 0.75rem;
  font-weight: 700;
}
</style>
